import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  showHome?: boolean;
  className?: string;
}

// Mapping des routes vers les labels français
const routeLabels: Record<string, string> = {
  dashboard: 'Tableau de bord',
  inventory: 'Inventaire',
  products: 'Produits',
  lots: 'Lots',
  history: 'Historique',
  reports: 'Rapports',
  analytics: 'Analytics',
  insights: 'Insights',
  settings: 'Paramètres',
  alerts: 'Alertes',
  recipes: 'Recettes',
  suppliers: 'Fournisseurs',
  'global-stocks': 'Stocks globaux',
  'history-reports': 'Rapports historiques',
};

/**
 * Composant Breadcrumbs pour la navigation
 *
 * @example
 * // Utilisation automatique basée sur l'URL
 * <Breadcrumbs />
 *
 * @example
 * // Utilisation avec items personnalisés
 * <Breadcrumbs items={[
 *   { label: 'Produits', href: '/products' },
 *   { label: 'Nouveau produit' }
 * ]} />
 */
export function Breadcrumbs({ items, showHome = true, className = '' }: BreadcrumbsProps) {
  const location = useLocation();

  // Générer les breadcrumbs automatiquement si pas d'items fournis
  const breadcrumbItems: BreadcrumbItem[] = items || generateBreadcrumbs(location.pathname);

  if (breadcrumbItems.length === 0 && !showHome) {
    return null;
  }

  return (
    <nav aria-label="Fil d'Ariane" className={`flex items-center text-sm ${className}`}>
      <ol className="flex items-center gap-1 flex-wrap">
        {showHome && (
          <li className="flex items-center">
            <Link
              to="/dashboard"
              className="flex items-center gap-1 text-gray-500 hover:text-primary transition-colors p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Accueil"
            >
              <Home size={16} />
              <span className="sr-only sm:not-sr-only">Accueil</span>
            </Link>
          </li>
        )}

        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;

          return (
            <li key={index} className="flex items-center">
              <ChevronRight size={16} className="text-gray-400 mx-1" aria-hidden="true" />
              {isLast || !item.href ? (
                <span
                  className="text-gray-900 dark:text-white font-medium px-1"
                  aria-current="page"
                >
                  {item.label}
                </span>
              ) : (
                <Link
                  to={item.href}
                  className="text-gray-500 hover:text-primary transition-colors p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * Génère les breadcrumbs à partir du pathname
 */
function generateBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  const items: BreadcrumbItem[] = [];

  let currentPath = '';

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    currentPath += `/${segment}`;

    // Ignorer le dashboard car c'est la home
    if (segment === 'dashboard') continue;

    const label = routeLabels[segment] || capitalizeFirst(segment);
    const isLast = i === segments.length - 1;

    items.push({
      label,
      href: isLast ? undefined : currentPath,
    });
  }

  return items;
}

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).replace(/-/g, ' ');
}
